import { Suspense, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteItem, fetchPaginationData } from "../../utils/apiUtils";
import DeleteModel from "../../Components/DeleteModel";
import Spinner from "../../Components/spinner";
import Breadcrumb from "../../Components/Breadcrumb";
import Pagination from "../../Components/Pagination";
import { DeleteSingleButton, ViewButton } from "../../Components/TableHelper";
import { formatDate } from "../../utils/constant";
import TableSearch from "../../Components/TableSearch";
import PerPageSelect from "../../Components/PerPageSelect";
import { useDebounce } from "../../hooks/useDebounce";
import Badge from "../../Components/Badge";
import { paymentStyle, statusStyle } from "../../utils/statusColor";
import OrdersFilter from "./components/OrdersFilter";
import OrderTop from "./OrderTop";

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [data, setdata] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteItemId, setDeleteItemId] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const navigator = useNavigate();

  const fetchDataPagination = async (
    page = 1,
    currentSearch = search,
    currentPerPage = perPage,
  ) => {
    const requestData = {
      perPage: currentPerPage,
      pageNo: page,
      search: currentSearch,
    };
    await fetchPaginationData(
      "order/getAllOrders",
      requestData,
      setdata,
      setTotalCount,
      setCurrentPage,
      setLoading,
    );
  };

  useEffect(() => {
    fetchDataPagination(1);
  }, []);

  const debouncedFetch = useDebounce((searchValue) => {
    fetchDataPagination(1, searchValue);
  }, 500);

  const handleSearchChange = (value) => {
    setSearch(value);
    debouncedFetch(value);
  };

  const handlePerPageChange = (value) => {
    setPerPage(value);
    fetchDataPagination(1, search, value);
  };

  const totalPages = Math.ceil(totalCount / perPage);

  const handlePageClick = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchDataPagination(page);
    }
  };

  // ✅ navigate to URL-based route — no more location.state
  const handleRedirect = (item) => {
    navigator(`/order-details/${item._id}`);
  };

  const handleDelete = async (data) => {
    setDeleteItemId(data);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteItem(
        "order/deleteOrder",
        deleteItemId?._id,
        fetchDataPagination,
      );
      setDeleteItemId({});
      setDeleteModalOpen(false);
      return res;
    } catch (error) {
      return error;
    } finally {
      setIsDeleting(false);
    }
  };

  const Tableheader = [
    "Order ID",
    "Customer",
    "Country",
    "Items",
    "Total (USD)",
    "Order Status",
    "Payment",
    "Last Updated",
    "Action",
  ];

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Orders", path: "/orders" },
  ];
  const handleFilterChange = useCallback((value) => {
    setFilters(value);
  }, []);
  return (
    <>
      <div className="content-wrapper">
        <div className="page-header m-0">
          <h3 className="custom-page-title">Orders</h3>
          <Breadcrumb items={breadcrumbdata} />
        </div>

        <OrderTop totalOrders={data?.totalOrders} confirmedOrders={data?.confirmedOrders} cancelOrders={data?.cancelOrders} pendingOrders={data?.pendingOrder} />
        <div className="d-flex justify-content-between align-items-center bg-white p-2 mt-n2 flex-wrap gap-2">
          <PerPageSelect value={perPage} onChange={handlePerPageChange} />

          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "10px" }}
          >
            {/* <OrdersFilter
              filters={filters}
              onFilterChange={handleFilterChange}
            /> */}

            <TableSearch
              value={search}
              onChange={handleSearchChange}
              placeholder="Search By Order ID..."
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: "10px" }}>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table
                    className="custom-table table table-striped"
                    id="orderstable"
                  >
                    <thead>
                      <tr>
                        {Tableheader.map((item, index) => (
                          <th className="font-weight-bold" key={index}>
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center">
                            <Spinner />
                          </td>
                        </tr>
                      ) : data?.getData?.length > 0 ? (
                        data?.getData?.map((item, index) => (
                          <tr key={item._id || index}>
                            <td
                              style={{
                                fontWeight: "600",
                                color: "#a1780e",
                                whiteSpace: "nowrap",
                              }}
                            >
                              #{item.orderDisplayId}
                            </td>
                            <td>
                              <div style={{ fontWeight: "500" }}>
                                {`${item.customerId?.firstName || ""} ${item.customerId?.lastName || ""}`.trim()}
                              </div>
                              <small className="text-muted">
                                {item?.customerId?.email}
                              </small>
                            </td>
                            <td>
                              {item?.shippingAddressId?.countryId
                                ?.countryName || "-"}
                            </td>
                            <td>{item?.items?.length || 0}</td>
                            <td>${Number(item.total ?? 0).toFixed(2)}</td>
                            <td>
                              <Badge
                                label={item?.orderStatusId?.orderStatus}
                                style={statusStyle(
                                  item.orderStatusId?.orderStatus,
                                )}
                              />
                            </td>
                            <td>
                              <Badge
                                label={item?.paymentStatusId?.paymentStatus}
                                style={paymentStyle(
                                  item.paymentStatusId?.paymentStatus,
                                )}
                              />
                            </td>
                            <td>{formatDate(item.createdAt)}</td>
                            <td>
                              <div className="d-flex" style={{ gap: "10px" }}>
                                <ViewButton
                                  handleRedirect={handleRedirect}
                                  item={item}
                                />
                                <DeleteSingleButton
                                  handleDelete={handleDelete}
                                  item={item}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            <h5>No data found.</h5>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {data?.getData && data?.getData?.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageClick}
                    perPage={perPage}
                    totalCount={totalCount}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteModalOpen && (
        <Suspense fallback={null}>
          <DeleteModel
            deleteModalOpen={deleteModalOpen}
            setDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            onConfirm={confirmDelete}
            itemName="Orders"
            isDeleting={isDeleting}
          />
        </Suspense>
      )}
    </>
  );
};

export default Orders;
